// Business tenant context (live mode only — mounted by BusinessShell inside
// the /business section when the business_accounting flag is on).
//
// Owns the user's business list and the ACTIVE business selection. Selection
// is persisted per-user in localStorage and pushed into apiUtils
// (setGlobalBusinessId) so business-scoped services send X-Business-Id.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Business,
  BusinessGroupAddition,
  CreateBusinessParams,
  UpdateBusinessParams,
  UUID,
} from '../types/businessApi';
import { businessService } from '../services/businessService';
import { businessPeriodCloseService } from '../services/businessPeriodCloseService';
import { businessExperienceService } from '../services/businessExperienceService';
import {
  clearBusinessExperience,
  setBusinessExperience,
} from '../services/businessExperienceStore';
import { refreshBillingAccounts } from '../services/billingAccountsStore';
import {
  clearBusinessModules,
  setBusinessModules,
  setFieldServiceSetUp,
  setPersonalServicesSetUp,
} from '../services/businessModulesStore';
import { fieldServiceSettingsService } from '../services/fieldServiceSettingsService';
import { FIELD_SERVICE_RELEASED } from '../components/fieldService/release';
import { personalServicesSettingsService } from '../services/personalServicesSettingsService';
import { PERSONAL_SERVICES_RELEASED } from '../components/personalServices/release';
import { isPersonalServicesRole } from '../components/personalServices/personalServicesAccess';
import { setGlobalBusinessId } from '../utils/apiUtils';
import { isReadRestrictedRole } from '../components/business/settings/roleCopy';
import { useAuth } from './AuthContext';

/**
 * The slice of period-close status the mutation gates need (PRD 08 §8.5).
 * Cached here rather than fetched per form: every date-bearing mutation
 * screen asks the same question, and the answer only changes on a close,
 * a reopen, or a business switch.
 */
export interface PeriodLockState {
  closedThrough: string | null;
  /** Owner or accountant — decides "Reopen books" vs "contact your accountant". */
  canReopen: boolean;
}

const STORAGE_PREFIX = 'business.selected.';

export const businessSelectionStorageKey = (userKey: string): string =>
  `${STORAGE_PREFIX}${userKey}`;

// The remembered selection is a convenience: storage can be missing or throw
// (a private window, blocked site data), and a throw here used to escape a
// switch and leave its caller stuck (W6 review #3). Unreadable reads as none;
// an unwritable write is skipped.
const readSelection = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const writeSelection = (key: string, businessId: string): void => {
  try {
    localStorage.setItem(key, businessId);
  } catch {
    // Not remembered; the selection still applies for this page.
  }
};

interface BusinessContextValue {
  businesses: Business[];
  currentBusiness: Business | null;
  isLoading: boolean;
  /** True once the initial business-list fetch has settled. */
  hasLoaded: boolean;
  error: string | null;
  /**
   * meta.can_create_business — whether the user's own subscription entitles
   * them to create a business. False + no businesses ⇒ teaser (preview) mode.
   */
  canCreateBusiness: boolean;
  /**
   * meta.group_additions — groups the user may add a business to, each priced
   * (PRD 42 §4.2). The way in for an owner whose group holds a subscription or
   * a comp, which `canCreateBusiness` deliberately does not count.
   */
  groupAdditions: BusinessGroupAddition[];
  /**
   * meta.paid_empty_group_id — a group the user paid for and has not named a
   * business in yet (PRD 86 §3), or null. Their first business is created into
   * it with `entity_group_id` and no `monthly_increase_cents`. Cleared once a
   * business is created into it, and re-read with the offers.
   */
  paidEmptyGroupId: UUID | null;
  /**
   * Re-read the offers and the paid group — after the price moved under a
   * confirmation (409), or a business joined a group.
   */
  refreshGroupAdditions: () => Promise<void>;
  /**
   * True while the ACTIVE business is entitlement-lapsed (access_mode
   * 'read_only') — mutation affordances gate on this via
   * useBusinessMutability(); reads, reports and exports stay available.
   */
  readOnly: boolean;
  /**
   * Closed-through date for the ACTIVE business, or null while unknown.
   * Consumed through useBusinessPeriodLock(date), never read directly.
   */
  periodLock: PeriodLockState | null;
  /** Re-read the close status — after a close, a reopen, or a 422. */
  refreshPeriodLock: () => Promise<void>;
  selectBusiness: (businessId: string) => void;
  createBusiness: (params: CreateBusinessParams) => Promise<Business>;
  /** Owner-only profile edit; the updated row replaces its entry in the list. */
  updateBusiness: (businessId: string, params: UpdateBusinessParams) => Promise<Business>;
  refresh: () => Promise<void>;
}

const BusinessContext = createContext<BusinessContextValue | null>(null);

/**
 * The business a deep link asked for, if any (PRD 63 §5.2). Kept beside the
 * provider rather than exported from a route file: the param is part of this
 * provider's contract, and the one place that writes it (clientRequestLinks)
 * names the same key.
 */
const requestedBusinessId = (): string | null => {
  try {
    return new URLSearchParams(window.location.search).get('business');
  } catch {
    return null;
  }
};

export const BusinessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const userKey = user?.uuid || user?.email || 'anonymous';
  const storageKey = businessSelectionStorageKey(userKey);

  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [currentBusinessId, setCurrentBusinessId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canCreateBusiness, setCanCreateBusiness] = useState(false);
  const [groupAdditions, setGroupAdditions] = useState<BusinessGroupAddition[]>([]);
  const [paidEmptyGroupId, setPaidEmptyGroupId] = useState<UUID | null>(null);
  const [periodLock, setPeriodLock] = useState<PeriodLockState | null>(null);

  // Push the selection into apiUtils SYNCHRONOUSLY, alongside the state
  // update — never from an effect. Children mount in the same commit that
  // resolves the selection, and React runs child effects before the parent's,
  // so a data-fetching child (any live /business route) would fire its first
  // businessApiFetch before X-Business-Id existed and throw "No active
  // business selected". Setting it here means the header is in place before
  // that commit is even rendered.
  const applyBusinessId = useCallback((businessId: string | null) => {
    setGlobalBusinessId(businessId);
    setCurrentBusinessId(businessId);
  }, []);

  const applySelection = useCallback(
    (list: Business[], preferredId: string | null): string | null => {
      const match = preferredId && list.find(b => b.id === preferredId);
      const selected = match ? match.id : list[0]?.id ?? null;
      applyBusinessId(selected);
      return selected;
    },
    [applyBusinessId]
  );

  const fetchBusinesses = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const {
        businesses: list,
        canCreateBusiness: entitled,
        groupAdditions: offers,
        paidEmptyGroupId: paidGroup,
      } = await businessService.listBusinesses();
      setBusinesses(list);
      setCanCreateBusiness(entitled);
      setGroupAdditions(offers);
      setPaidEmptyGroupId(paidGroup);
      // A DEEP LINK MAY NAME THE BOOKS (PRD 63 §5.2). `/requests` renders above
      // this provider — it is a sibling of the /business subtree, not a child
      // — so a screen there has no `selectBusiness` to call before it
      // navigates, and a client with two businesses would otherwise land on
      // whichever one they used last with a link that said which one it meant.
      //
      // Read off `window.location` rather than useSearchParams: this provider
      // is mounted in tests and stories without a router, and the param is a
      // one-time hint, not state to subscribe to.
      const requested = requestedBusinessId();
      const persisted = readSelection(storageKey);
      const selected = applySelection(list, requested ?? persisted);
      // Persisted only when it was honoured, so a link into somebody else's
      // books cannot silently move where this browser opens next time.
      if (requested && selected === requested) writeSelection(storageKey, selected);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load businesses');
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  }, [storageKey, applySelection]);

  useEffect(() => {
    fetchBusinesses();
  }, [fetchBusinesses]);

  // Clear the module-level business id when the business section unmounts —
  // the value itself is set eagerly by applyBusinessId, not here.
  useEffect(() => () => setGlobalBusinessId(null), []);

  const selectBusiness = useCallback(
    (businessId: string) => {
      applyBusinessId(businessId);
      writeSelection(storageKey, businessId);
      // The sidebar's billing badge sits above this provider, so it hears of
      // a switch only through its store (PRD 114 §4.4).
      refreshBillingAccounts();
    },
    [storageKey, applyBusinessId]
  );

  // Only the offers, never the selection: a full refetch re-reads a deep
  // link's ?business= and could move the user off the business they just made.
  // The paid group rides the same response. createBusiness also clears it the
  // moment a business is named into it, since a failed re-read keeps it.
  const refreshGroupAdditions = useCallback(async () => {
    try {
      const { groupAdditions: offers, paidEmptyGroupId: paidGroup } =
        await businessService.listBusinesses();
      setGroupAdditions(offers);
      setPaidEmptyGroupId(paidGroup);
    } catch {
      // Keep the offers we had; the api re-checks every price on the write.
    }
  }, []);

  const createBusiness = useCallback(
    async (params: CreateBusinessParams): Promise<Business> => {
      const business = await businessService.createBusiness(params);
      setBusinesses(prev => [...prev, business]);
      applyBusinessId(business.id);
      writeSelection(storageKey, business.id);
      // The group just grew, so the price of the NEXT one may have moved.
      if (params.entity_group_id) {
        // A paid group named just now is no longer waiting for its business:
        // the 201 proves it. Cleared here, not left to the re-read, which
        // keeps the old value when it fails — and then the screens would
        // still offer that group as "already paid for", where the next
        // business is a priced addition.
        const named = params.entity_group_id;
        setPaidEmptyGroupId(prev => (prev === named ? null : prev));
        void refreshGroupAdditions();
      }
      return business;
    },
    [storageKey, applyBusinessId, refreshGroupAdditions]
  );

  // A field role reads no period-close status (PRD 89 §4.9): the endpoint
  // answers it 403 role_forbidden, and the status only feeds dated ledger
  // forms the role never opens. Asking anyway would mark whatever page is open
  // as "not part of your role", so the question is not asked.
  const currentRole = useMemo(
    () => businesses.find(b => b.id === currentBusinessId)?.role ?? null,
    [businesses, currentBusinessId]
  );
  const readRestricted = isReadRestrictedRole(currentRole);

  const refreshPeriodLock = useCallback(async () => {
    if (!currentBusinessId || readRestricted) {
      setPeriodLock(null);
      return;
    }
    try {
      const status = await businessPeriodCloseService.getStatus();
      setPeriodLock({
        closedThrough: status.books_closed_through,
        canReopen: status.can_close,
      });
    } catch {
      // Unknown beats wrong: leaving it null means the gates stay open and
      // the server still refuses anything genuinely locked.
      setPeriodLock(null);
    }
  }, [currentBusinessId, readRestricted]);

  useEffect(() => {
    void refreshPeriodLock();
  }, [refreshPeriodLock]);

  // The resolved experience is published to a module-level store rather than
  // held in this context, because its most important consumer — the sidebar —
  // is mounted ABOVE this provider in the route tree (see
  // businessExperienceStore). This context owns "which business", so it is the
  // right place to fetch from.
  const refreshExperience = useCallback(async () => {
    if (!currentBusinessId) {
      clearBusinessExperience();
      return;
    }
    // Cleared BEFORE the fetch, not after: otherwise the nav spends a request
    // filtering the incoming business by the outgoing one's experience, which
    // looks exactly like a menu row vanishing at random.
    clearBusinessExperience();
    try {
      setBusinessExperience(await businessExperienceService.get());
    } catch {
      // Unknown beats wrong, and here "unknown" means SHOW EVERYTHING. A
      // failed fetch must never be the reason a customer cannot find a
      // feature, so null resolves to the unfiltered menu.
      setBusinessExperience(null);
    }
  }, [currentBusinessId]);

  useEffect(() => {
    void refreshExperience();
  }, [refreshExperience]);

  // Leaving the business section drops the experience with the business id, so
  // a later mount never filters against a stale tenant's preference.
  useEffect(() => () => clearBusinessExperience(), []);

  const updateBusiness = useCallback(
    async (businessId: string, params: UpdateBusinessParams): Promise<Business> => {
      const updated = await businessService.updateBusiness(businessId, params);
      setBusinesses(prev => prev.map(item => (item.id === updated.id ? updated : item)));
      return updated;
    },
    []
  );

  const refresh = useCallback(async () => {
    await fetchBusinesses();
  }, [fetchBusinesses]);

  const currentBusiness = useMemo(
    () => businesses.find(b => b.id === currentBusinessId) ?? null,
    [businesses, currentBusinessId]
  );

  const readOnly = currentBusiness?.access_mode === 'read_only';

  // The modules and the field service set-up state go to a module-level store
  // as well (PRD 88 §4.1), for the reason the experience does: the sidebar is
  // mounted above this provider and draws a module's rows from them.
  // Cannabis's row also reads the restriction and the member's role (PRD 111
  // §4.1, cannabisNavAccess) — both already on the payload, so no request.
  const currentModules = currentBusiness?.modules ?? null;
  const currentRestrictedIndustry = currentBusiness?.restricted_industry ?? null;
  useEffect(() => {
    if (!currentBusinessId) {
      clearBusinessModules();
      return;
    }
    setBusinessModules(currentBusinessId, currentModules, {
      restrictedIndustry: currentRestrictedIndustry,
      role: currentRole,
    });
  }, [currentBusinessId, currentModules, currentRestrictedIndustry, currentRole]);

  // Has this business set field service up? Asked only where the answer can
  // change what draws (fieldServiceAccess):
  //   - never for a field role, which the api answers 403 role_forbidden for a
  //     technician — and needs no asking: a dispatcher or technician can only
  //     be granted where field service was set up (ROLE_PREREQUISITES), and a
  //     set-up row is never deleted;
  //   - WHILE UNRELEASED, only for a business holding the module, so no other
  //     business on prod sends a request about an unreleased module;
  //   - after release, for every other business, because a lapsed one keeps
  //     its rows and one that never set up sees the Overview alone.
  // Unknown on any failure, which draws the least.
  const fieldServiceHeld = currentModules?.field_service === true;
  const refreshFieldServiceSetUp = useCallback(async () => {
    if (!currentBusinessId) return;
    if (readRestricted) {
      setFieldServiceSetUp(currentBusinessId, true);
      return;
    }
    if (!fieldServiceHeld && !FIELD_SERVICE_RELEASED) return;
    try {
      const { set_up: setUp } = await fieldServiceSettingsService.get();
      setFieldServiceSetUp(currentBusinessId, setUp);
    } catch {
      setFieldServiceSetUp(currentBusinessId, null);
    }
  }, [currentBusinessId, readRestricted, fieldServiceHeld]);

  useEffect(() => {
    void refreshFieldServiceSetUp();
  }, [refreshFieldServiceSetUp]);

  // Has this business set personal services up (PRD 121 §4.2)? Field
  // service's rule, with the module's own two roles:
  //   - front desk and provider need no asking: either can only be granted
  //     where personal services is set up (ROLE_PREREQUISITES), and a set-up
  //     row is never deleted;
  //   - any other read-restricted role (the field roles, the register) is
  //     never asked — the api answers it 403 role_forbidden;
  //   - WHILE UNRELEASED, only a business holding the module is asked, so no
  //     other business on prod sends a request about an unreleased module.
  // Unknown on any failure, which draws the least.
  const personalServicesHeld = currentModules?.personal_services === true;
  const personalServicesRole = isPersonalServicesRole(currentRole);
  const refreshPersonalServicesSetUp = useCallback(async () => {
    if (!currentBusinessId) return;
    if (personalServicesRole) {
      setPersonalServicesSetUp(currentBusinessId, true);
      return;
    }
    if (readRestricted) return;
    if (!personalServicesHeld && !PERSONAL_SERVICES_RELEASED) return;
    try {
      const { set_up: setUp } = await personalServicesSettingsService.get();
      setPersonalServicesSetUp(currentBusinessId, setUp);
    } catch {
      setPersonalServicesSetUp(currentBusinessId, null);
    }
  }, [currentBusinessId, personalServicesRole, readRestricted, personalServicesHeld]);

  useEffect(() => {
    void refreshPersonalServicesSetUp();
  }, [refreshPersonalServicesSetUp]);

  // Leaving the business section drops them with the business id.
  useEffect(() => () => clearBusinessModules(), []);

  const value = useMemo(
    () => ({
      businesses,
      currentBusiness,
      isLoading,
      hasLoaded,
      error,
      canCreateBusiness,
      groupAdditions,
      paidEmptyGroupId,
      refreshGroupAdditions,
      readOnly,
      periodLock,
      refreshPeriodLock,
      selectBusiness,
      createBusiness,
      updateBusiness,
      refresh,
    }),
    [
      businesses,
      currentBusiness,
      isLoading,
      hasLoaded,
      error,
      canCreateBusiness,
      groupAdditions,
      paidEmptyGroupId,
      refreshGroupAdditions,
      readOnly,
      periodLock,
      refreshPeriodLock,
      selectBusiness,
      createBusiness,
      updateBusiness,
      refresh,
    ]
  );

  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
};

export function useBusiness(): BusinessContextValue {
  const ctx = useContext(BusinessContext);
  if (!ctx) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return ctx;
}

/** Null outside a BusinessProvider (i.e. in preview mode). */
export function useBusinessOptional(): BusinessContextValue | null {
  return useContext(BusinessContext);
}
